import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Upload, Music as MusicIcon } from "lucide-react";

const EMPTY = {
  type: "drum",
  name: "",
  description: "",
  wood: "",
  dimensions: "",
  price: "",
  image: "",
  audio: "",
  featured: false,
};

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB cap per upload

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function InstrumentForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const imageRef = useRef(null);
  const audioRef = useRef(null);

  const editing = !!initial?.id;

  const handleFile = async (which, file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setErr(`File too large (max ${MAX_BYTES / 1024 / 1024} MB).`);
      return;
    }
    setErr(null);
    const dataUrl = await readFileAsDataUrl(file);
    setForm((f) => ({ ...f, [which]: dataUrl }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      if (editing) {
        const { data } = await api.put(`/admin/instruments/${initial.id}`, form);
        onSaved(data, "updated");
      } else {
        const { data } = await api.post(`/admin/instruments`, form);
        onSaved(data, "created");
      }
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="instrument-dialog">
      <div className="bg-white w-full max-w-2xl rounded-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] sticky top-0 bg-white">
          <h2 className="font-display text-2xl">{editing ? "Edit instrument" : "Add new instrument"}</h2>
          <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="close-dialog">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="px-6 py-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Type</label>
              <select
                className="input-field"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="form-type"
              >
                <option value="drum">Drum</option>
                <option value="didgeridoo">Didgeridoo</option>
                <option value="flute">Pharaoh Love Flute</option>
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Name *</label>
              <input
                required
                className="input-field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="form-name"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Description</label>
            <textarea
              className="input-field"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="form-description"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Wood / material</label>
              <input className="input-field" value={form.wood} onChange={(e) => setForm({ ...form, wood: e.target.value })} data-testid="form-wood" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Dimensions</label>
              <input className="input-field" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} data-testid="form-dimensions" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Price</label>
              <input className="input-field" placeholder='e.g. $480 or "Inquire"' value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
            </div>
          </div>

          {/* Image */}
          <div>
            <label className="label-eyebrow block mb-2">Image</label>
            <div className="flex items-start gap-4">
              {form.image ? (
                <img src={form.image} alt="preview" className="w-28 h-28 rounded-md object-cover border border-[var(--border)]" />
              ) : (
                <div className="w-28 h-28 rounded-md bg-[var(--bg-secondary)] border border-dashed border-[var(--border)] flex items-center justify-center text-xs text-[var(--text-secondary)] text-center px-2">No image</div>
              )}
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={imageRef}
                  hidden
                  onChange={(e) => handleFile("image", e.target.files?.[0])}
                  data-testid="form-image-file"
                />
                <button type="button" onClick={() => imageRef.current?.click()} className="btn-outline text-sm py-2 px-4" data-testid="form-image-upload">
                  <Upload size={14} /> Upload image
                </button>
                <input
                  className="input-field text-xs"
                  placeholder="...or paste image URL"
                  value={form.image.startsWith("data:") ? "" : form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  data-testid="form-image-url"
                />
                {form.image && (
                  <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => setForm({ ...form, image: "" })} data-testid="form-image-clear">
                    Clear image
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Audio */}
          <div>
            <label className="label-eyebrow block mb-2">Audio sample</label>
            <div className="flex items-start gap-4">
              <div className="w-28 h-28 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)] flex items-center justify-center text-[var(--text-secondary)]">
                <MusicIcon size={24} />
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="audio/*"
                  ref={audioRef}
                  hidden
                  onChange={(e) => handleFile("audio", e.target.files?.[0])}
                  data-testid="form-audio-file"
                />
                <button type="button" onClick={() => audioRef.current?.click()} className="btn-outline text-sm py-2 px-4" data-testid="form-audio-upload">
                  <Upload size={14} /> Upload audio
                </button>
                <input
                  className="input-field text-xs"
                  placeholder="...or paste audio URL (mp3/wav)"
                  value={form.audio.startsWith("data:") ? "" : form.audio}
                  onChange={(e) => setForm({ ...form, audio: e.target.value })}
                  data-testid="form-audio-url"
                />
                {form.audio && (
                  <>
                    <audio src={form.audio} controls className="w-full mt-1" />
                    <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => setForm({ ...form, audio: "" })} data-testid="form-audio-clear">
                      Clear audio
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              data-testid="form-featured"
            />
            Featured (will show a badge on the public site)
          </label>

          {err && <p className="text-sm text-red-700" data-testid="form-error">{err}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm py-2 px-5" data-testid="form-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5" data-testid="form-save">
              {saving ? "Saving..." : editing ? "Save changes" : "Add instrument"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminInstruments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/instruments");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this instrument? This cannot be undone.")) return;
    await api.delete(`/admin/instruments/${id}`);
    setItems((arr) => arr.filter((i) => i.id !== id));
  };

  const onSaved = (saved) => {
    setItems((arr) => {
      const idx = arr.findIndex((i) => i.id === saved.id);
      if (idx >= 0) {
        const copy = [...arr];
        copy[idx] = saved;
        return copy;
      }
      return [saved, ...arr];
    });
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <div data-testid="admin-instruments-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow mb-2">Studio · Manage</p>
          <h1 className="font-display text-4xl">Instruments</h1>
        </div>
        <div className="flex items-center gap-3">
          <select className="input-field text-sm py-2" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="filter-type">
            <option value="all">All ({items.length})</option>
            <option value="drum">Drums</option>
            <option value="didgeridoo">Didgeridoos</option>
            <option value="flute">Love Flutes</option>
          </select>
          <button className="btn-primary text-sm py-2 px-5" onClick={() => setEditing(EMPTY)} data-testid="add-instrument-btn">
            <Plus size={16} /> Add instrument
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-[var(--text-secondary)]">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <p className="text-[var(--text-secondary)]">No instruments yet. Add your first piece.</p>
        </div>
      ) : (
        <div className="admin-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-secondary)]/60 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Image</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Price</th>
                <th className="px-4 py-3 font-medium">Audio</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-[var(--border)]" data-testid={`row-${it.id}`}>
                  <td className="px-4 py-3">
                    {it.image ? (
                      <img src={it.image} alt="" className="w-14 h-14 rounded object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded bg-[var(--bg-secondary)]" />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {it.name}
                    {it.featured && <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--accent)]">Featured</span>}
                  </td>
                  <td className="px-4 py-3 capitalize">{it.type}</td>
                  <td className="px-4 py-3">{it.price || "—"}</td>
                  <td className="px-4 py-3">{it.audio ? "✓" : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(it)} className="p-2 hover:text-[var(--accent)]" data-testid={`edit-${it.id}`}>
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => onDelete(it.id)} className="p-2 hover:text-red-700" data-testid={`delete-${it.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <InstrumentForm initial={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
